<template>
  <div :class="[paddingClass]">
    <div :class="[headerClasses, 'p-5 rounded-xl border flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-6']">
      <!-- Left: Back Button + Title Section -->
      <div class="flex items-center space-x-4 flex-shrink-0">
        <!-- Back Button (optional) -->
        <template v-if="showBackButton">
          <component
            :is="backButtonComponent"
            :to="backTo"
            @click="computedBackHandler"
            class="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors cursor-pointer bg-transparent border-0 p-0 m-0 shadow-none outline-none"
            :title="backTitle"
            :aria-label="backTitle"
          >
            <slot name="back-icon">
              <Icon name="arrowLeft" size="lg" class="w-6 h-6" />
            </slot>
          </component>
          
          <div class="h-8 w-px bg-gray-200 dark:bg-gray-700"></div>
        </template>
        
        <!-- Title Section -->
        <div class="space-y-1">
          <Heading :level="titleVariant" :class="['flex items-center', titleClass]">
            <!-- Icon (slot or Icon component) -->
            <template v-if="iconPath">
              <Icon 
                :name="getIconNameFromPath(iconPath)"
                :class="iconClass"
              />
            </template>
            <slot name="icon" v-else-if="$slots.icon"></slot>
            
            <!-- Title -->
            <slot name="title">{{ title }}</slot>
          </Heading>
          
          <!-- Description -->
          <Text 
            v-if="welcomeUser || computedDescription || $slots.description" 
            :variant="descriptionVariant"
            color="secondary"
          >
            <slot name="description">
              <span v-if="welcomeUser">
                {{ welcomePrefix }}<Text as="span" weight="semibold" color="primary">{{ welcomeUser }}</Text>{{ welcomeSuffix }}
              </span>
              <span v-else>{{ computedDescription }}</span>
            </slot>
          </Text>
          
          <!-- Helper Text (optional) -->
          <Text 
            v-if="helperTextMobile || helperTextDesktop || computedHelperText || $slots.helperText" 
            :variant="helperTextVariant"
            color="muted"
            class="flex items-center"
          >
            <Icon v-if="showHelperIcon" name="infoFilled" class="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2 text-yellow-500 flex-shrink-0" />
            <slot name="helperText">
              <span v-if="helperTextMobile || helperTextDesktop">
                <span class="hidden sm:inline">{{ helperTextDesktop || computedHelperText }}</span>
                <span class="sm:hidden">{{ helperTextMobile || computedHelperText }}</span>
              </span>
              <span v-else>{{ computedHelperText }}</span>
            </slot>
          </Text>
        </div>
      </div>
      
      <!-- Center: Custom Content (Search, etc.) -->
      <div v-if="$slots.center" class="flex-1 flex items-center justify-center px-4 min-w-0">
        <slot name="center"></slot>
      </div>
      
      <!-- Right: Actions / Custom Content -->
      <div v-if="hasActions" :class="[actionsClass, 'flex-shrink-0 w-full lg:w-auto']">
        <!-- Slot-based actions (for full customization) -->
        <slot name="actions">
          <!-- Prop-based action buttons (for quick usage) -->
          <div v-if="actionButtons && actionButtons.length > 0" class="flex flex-wrap items-center gap-2 md:gap-3 w-full lg:w-auto">
            <HLButton
              v-for="(button, index) in actionButtons"
              :key="index"
              :id="`page-header-btn-${index}`"
              @click="handleButtonClick(button, $event)"
              :variant="mapButtonVariant(button.variant)"
              :color="mapButtonColor(button.variant, button.color)"
              :size="button.size || 'md'"
              :disabled="button.disabled"
              :class="button.class || 'flex-1 min-w-[120px] lg:flex-initial lg:min-w-0'"
            >
              <template v-if="button.icon" #iconLeft>
                <Icon 
                  :name="getIconNameFromPath(button.icon)"
                  size="sm"
                  :class="{ 'animate-spin': button.loading && button.icon === ICONS.refresh }"
                />
              </template>
              <span :class="button.labelClass || 'hidden md:inline'">{{ button.label }}</span>
              <span v-if="button.mobileLabel" class="md:hidden">{{ button.mobileLabel }}</span>
            </HLButton>
          </div>
          
          <!-- Default slot fallback -->
          <slot></slot>
        </slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, useSlots } from 'vue'
import { useRouter } from 'vue-router'
import { PAGE_HEADER_COLORS } from '@/constants/uiColors'
import { ICONS } from '@/constants/icons'
import { HLButton } from '@platform-ui/highrise'
import Heading from '@/components/ui/Heading.vue'
import Text from '@/components/ui/Text.vue'
import Icon from '@/components/ui/Icon.vue'
import { t } from '@/i18n/en-us'

const router = useRouter()

const props = defineProps({
  // Title and description
  title: {
    type: String,
    default: ''
  },
  description: {
    type: String,
    default: ''
  },
  helperText: {
    type: String,
    default: ''
  },
  
  // Enhanced description patterns
  welcomeUser: {
    type: String,
    default: ''
  },
  
  // Responsive helper text
  helperTextMobile: {
    type: String,
    default: ''
  },
  helperTextDesktop: {
    type: String,
    default: ''
  },
  
  // Control helper text icon
  showHelperIcon: {
    type: Boolean,
    default: true
  },
  
  // Action buttons (declarative API)
  actionButtons: {
    type: Array,
    default: () => []
    // Format: [{ 
    //   label, mobileLabel?, icon, onClick, 
    //   variant?: 'primary'|'secondary'|'outline'|'ghost'|'danger'|'success'|'warning'|'link',
    //   color?: 'blue'|'gray'|'red'|'orange'|'green' or custom hex,
    //   size?: 'xs'|'sm'|'md'|'lg'|'xl',
    //   loading?, disabled?, class?, labelClass? 
    // }]
  },
  
  // Icon configuration
  iconPath: {
    type: String,
    default: ''
  },
  iconClass: {
    type: String,
    default: 'w-6 h-6 md:w-8 md:h-8 mr-2 md:mr-3 text-primary-600 dark:text-primary-400'
  },
  
  // Back button configuration
  showBackButton: {
    type: Boolean,
    default: false
  },
  backTo: {
    type: [String, Object],
    default: '/'
  },
  backTitle: {
    type: String,
    default: () => t('pageHeader.backToDashboard')
  },
  backHandler: {
    type: Function,
    default: null
  },
  useHistoryBack: {
    type: Boolean,
    default: false
  },
  
  // Styling
  padding: {
    type: String,
    default: 'p-4 md:p-6',
    validator: (value) => ['p-4', 'p-6', 'p-4 md:p-6'].includes(value)
  },
  
  // Typography variants (using Text/Heading components)
  titleVariant: {
    type: String,
    default: 'h4',
    validator: (value) => ['h1', 'h2', 'h3', 'h4'].includes(value)
  },
  descriptionVariant: {
    type: String,
    default: 'body-lg',
    validator: (value) => ['body', 'body-lg', 'body-sm'].includes(value)
  },
  helperTextVariant: {
    type: String,
    default: 'caption',
    validator: (value) => ['caption', 'body-sm'].includes(value)
  },
  titleClass: {
    type: String,
    default: 'my-2'
  }
})

// Get slots
const slots = useSlots()

// Computed properties for content
const computedDescription = computed(() => {
  return props.description
})

const computedHelperText = computed(() => {
  return props.helperText
})

// Welcome message parsing
const welcomePrefix = computed(() => {
  const template = 'Welcome back, '
  return template
})

const welcomeSuffix = computed(() => {
  return '!'
})

// Check if actions section should be rendered
const hasActions = computed(() => {
  return props.actionButtons?.length > 0 || slots.actions || slots.default
})

// Computed classes
const headerClasses = computed(() => {
  return `${PAGE_HEADER_COLORS.gradient} ${PAGE_HEADER_COLORS.borderColor}`
})

const paddingClass = computed(() => props.padding)

const actionsClass = computed(() => {
  return 'flex items-center space-x-3'
})

// Determine if back button should be a router-link or button
const backButtonComponent = computed(() => {
  return (props.backHandler || props.useHistoryBack) ? 'button' : 'router-link'
})

// Default back handler with history navigation
const defaultBackHandler = () => {
  // Check if there's a previous page in history (not the first page)
  if (window.history.length > 1) {
    router.go(-1) // Go back to previous page
  } else {
    router.push('/dashboard') // Default to dashboard if no history
  }
}

// Computed back handler - use custom handler, history back, or default router-link behavior
const computedBackHandler = computed(() => {
  if (props.backHandler) {
    return props.backHandler
  } else if (props.useHistoryBack) {
    return defaultBackHandler
  }
  return null
})

// Handle button click - execute onClick and remove focus
const handleButtonClick = (button, event) => {
  if (button.onClick) {
    button.onClick()
  }
  // Remove focus from the button to prevent blue border
  if (event && event.target) {
    event.target.blur()
  }
}

// Helper function to extract icon name from ICONS constant path
// ICONS.dashboard -> 'dashboard', ICONS.refresh -> 'refresh'
const getIconNameFromPath = (iconPath) => {
  if (!iconPath) return ''
  
  // Find the icon name by matching the path value with ICONS constant
  for (const [name, path] of Object.entries(ICONS)) {
    if (path === iconPath) {
      return name
    }
  }
  
  // Fallback: return empty string if not found
  return ''
}

// Helper function to map old button variants to HLButton variants
const mapButtonVariant = (variant) => {
  const variantMap = {
    'primary': 'primary',
    'secondary': 'secondary',
    'outline': 'secondary',  // HLButton's secondary is outlined
    'ghost': 'ghost',
    'link': 'text',
    'danger': 'secondary',   // Use with color='red'
    'success': 'secondary',  // Use with color='green'
    'warning': 'secondary'   // Use with color='orange'
  }
  
  return variantMap[variant] || 'secondary'
}

// Helper function to map button colors based on variant and explicit color
const mapButtonColor = (variant, explicitColor) => {
  // If explicit color is provided, use it
  if (explicitColor) return explicitColor
  
  // Map old semantic variants to appropriate colors
  const colorMap = {
    'danger': 'red',
    'success': 'green',
    'warning': 'orange',
    'primary': 'blue'
  }
  
  return colorMap[variant] || 'gray'
}
</script>

